<template>
  <div>
    <TransactionalPageBreadcrumbs
      class="my-4"
      :city-name="citySlugLocalityName"
      :city-slug="route.params.city"
      :category-slug="categSlug"
      :tag-slug="tagSlug"
      :general-slug="generalSlug"
      :type="pageType"
      :event-list-page-obj="eventListPageData"
    />

    <div class="event-list-page__section mb-0">
      <div
        class="relative h-[80px] md:h-auto rounded-xl overflow-hidden md:overflow-visible flex items-center"
      >
        <NuxtImg
          :preload="true"
          class="card-image object-cover w-full md:hidden"
          src="/_nuxt3-static/general/transactional-pages-banner.webp"
          alt="Ein Barkeeper gießt Cocktails in zwei Gläser."
          loading="eager"
        />

        <div
          class="w-full h-full bg-blend-hard-light bg-gradient-to-r from-white to-transparent absolute"
        ></div>

        <Heading
          v-if="searchHeading"
          class="absolute md:relative"
          h-class="h5 !text-teal-900"
          :title="searchHeading"
          :level="1"
        >
        </Heading>
      </div>
    </div>
    <FilteredProductShowcase
      :page-type="pageType"
      :event-list-page-data="eventListPageData"
      :load-schema="true"
    ></FilteredProductShowcase>

    <NuxtLazyHydrate when-visible>
      <ProductBoxShowcase
        v-if="['category', 'city-category'].includes(pageType)"
        :title="
          $t('eventListPage.kitsTitle', {
            category: capitalizedCategorySlug,
          })
        "
        title-class="legacy-h3"
        :category="categSlug"
        :is-duration-visible="false"
        is-mobile-slider
      />
    </NuxtLazyHydrate>
    <NuxtLazyHydrate when-visible>
      <TransactionalPageCityCategoriesList
        v-if="EventListPageType.CITY === pageType"
        :subtitle="eventListPageData?.attributes?.title"
        :city-slug="citySlug"
        :category-slug="categSlug"
        class="pt-10"
        :section-title="
          $t('eventListPage.city.cityCategoryHorizontalListTitle', {
            city: citySlugLocalityName,
          })
        "
        data-test-id="city-category-horizontal-list"
      />
    </NuxtLazyHydrate>
    <NuxtLazyHydrate when-visible>
      <TransactionalPageCardListFromStrapi
        v-if="
          strapiEventListPageGetters.getHorizontalCardList(eventListPageData)
            .length > 0
        "
        :event-list-page="eventListPageData"
        class="pt-10"
        data-test-id="horizontal-card-list"
      />
    </NuxtLazyHydrate>
    <NuxtLazyHydrate when-visible>
      <vue-markdown
        class="prose my-5"
        :source="eventListPageAttributes?.description || ''"
      ></vue-markdown>
    </NuxtLazyHydrate>

    <div class="mt-10">
      <Heading
        :level="2"
        :title="strapiEventListPageGetters.getShowcaseTitle(eventListPageData)"
        class="kft-heading--left mb-10"
      />
      <NuxtLazyHydrate when-visible>
        <SimpleCardShowcase
          :showcase-items="cardShowcase"
          data-test-id="simple-showcase"
        />
      </NuxtLazyHydrate>
    </div>
  </div>
</template>

<script setup lang="ts">
import VueMarkdown from 'vue-markdown-render'
import {
  EventListPageType,
  type EventListPageTypeString,
  type StrapiEventListPage,
} from '~/composables/types/strapiEventListPage'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import { useTransactionalPageResource } from '~/composables/transactionalPage/useTransactionalPageResource'
import { eventListPageLocalitiesUrl } from '~/components/transactionalPage/requestParams'
import { dynamicContentTransformer } from '~/utils/dynamicContentTransformer'

const route = useRoute()
let citySlug: string | undefined = route?.params?.city?.toString()
const categSlug = route.params?.category?.toString()
let generalSlug = route.params?.slug?.toString()
let tagSlug = generalSlug

const props = withDefaults(
  defineProps<{
    pageType: EventListPageTypeString
  }>(),
  {}
)

const { t } = useI18n()
const localisedTeamEventSlugPrefix = t('eventListPage.teamEventSlugPrefix')

if (props.pageType === EventListPageType.TEAMEVENT) {
  generalSlug = `${localisedTeamEventSlugPrefix}-${citySlug}`
  tagSlug = `${localisedTeamEventSlugPrefix}-${citySlug}`
  citySlug = undefined
}

const { locale } = useI18n()

const { data: dynamicContent } = await useFetchEventListPageDynamicContent(
  props.pageType,
  { categorySlug: categSlug, citySlug, generalSlug }
)

const strapiEventListPageWithNoLocaleFilter = await useStrapiEventListPage({
  slug: generalSlug,
  citySlug,
  categorySlug: categSlug,
})

const transactionalPageResource = await useTransactionalPageResource({
  strapiEventListPageWithNoLocaleFilter,
})

const eventListPageData = computed<StrapiEventListPage>(() => {
  // This takes in consideration that we have less than 10 languages
  // Which means that the paginated results will always include
  // The current translation
  //
  // Past 10 languages, this code would not find some of them
  const entry =
    transactionalPageResource?.data?.value?.find(
      (entry) => entry.attributes.locale.toLowerCase() === locale.value
    ) || transactionalPageResource?.data?.value?.[0]

  const returnObj =
    strapiEventListPageGetters.getLocalizationByLocale(entry, locale.value) ||
    entry

  if (dynamicContent.value?.data) {
    return dynamicContentTransformer(returnObj, dynamicContent.value.data)
  }

  return returnObj
})

if (!eventListPageData.value) {
  throw showError({ statusCode: 404, statusMessage: 'Page Not Found' })
}

const eventListPageAttributes = computed(() => {
  return eventListPageData?.value?.attributes
})

const cardShowcase = computed(() => {
  return Object.keys(eventListPageAttributes.value?.showcase || {})
    ?.map((key) => eventListPageAttributes.value?.showcase[key])
    .filter((cardDetails) => cardDetails?.id)
    .map((cd) => ({
      ...cd,
      imageUrl: strapiImageGetters.getImageUrl(cd.image.data),
    }))
})

const searchHeading = computed(
  () =>
    strapiEventListPageGetters.getSearchHeading(eventListPageData.value) ||
    strapiEventListPageGetters.getTitle(eventListPageData.value)
)

const shouldFetchLocalities = [
  EventListPageType.CITY_CATEGORY,
  EventListPageType.CITY,
].includes(props.pageType)

// Needed in:
// 1. city page for the horizontal city-category list
// 2. city-category page for the breadcrumbs
const { data: localityResponse } =
  (shouldFetchLocalities &&
    (await useKftApiFetch<KftApiDefaultResponse<{ name: string }>>(
      eventListPageLocalitiesUrl({ citySlug })
    ))) ||
  {}

const citySlugLocalityName = localityResponse?.value?.data.name

const capitalizedCategorySlug = computed(() => {
  if (!categSlug) return ''

  if (categSlug.toLowerCase() === 'diy') return 'DIY'

  return categSlug.charAt(0).toUpperCase() + categSlug.slice(1)
})
</script>

<style lang="postcss">
.prose {
  max-width: unset;
  table {
    width: unset;
  }
}
</style>
